<template>
  <div
    v-intersection="handleIntersection"
    class="c-chart-library-chart"
    :class="{ 'c-chart-library-chart--isolated': isolateLayout }"
    data-testid="chart-library-chart"
    :style="chartStyle"
  >
    <BaseDataLoadingWrapper
      class="column no-wrap"
      :state="state"
      @reload="emit('reload')"
    >
      <slot name="before" />

      <template
        v-if="$slots.loading"
        #loading
      >
        <slot name="loading" />
      </template>

      <Component
        :is="chartComponent"
        v-if="isIntersecting"
        v-bind="chartProps"
        class="c-chart-library-chart__chart fit self-center"
        :interaction-enabled="Boolean(chartConfig.interactionEnabled)"
        @chart-clicked="emit('chart-clicked', $event)"
        @chart:render="emit('chart:render', $event)"
      />
    </BaseDataLoadingWrapper>
  </div>
</template>

<script setup lang="ts">
import type { Chart } from "chart.js";
import { merge } from "lodash-es";
import type { IntersectionValue } from "quasar";
import { type Component, computed, type CSSProperties, ref } from "vue";

import { appInstance } from "shared/boot/app";
import { BaseDataLoadingWrapper } from "shared/components/base";
import type { ChartConfig } from "shared/modules/Charts/types/chartConfig";
import type { ChartClickEvent } from "shared/modules/Charts/types/chartInteractions";
import type { ChartLibraryProps } from "shared/modules/Charts/types/data";
import { DataLoadingWrapperState } from "shared/types";

const props = defineProps<{
  chartConfig: ChartConfig;
  chartData: ChartLibraryProps;
  error?: boolean;
  intersectOnce?: boolean;
  isolateLayout?: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  "chart-clicked": [ChartClickEvent];
  "chart:render": [Chart];
  intersection: [boolean];
  reload: [];
}>();

const isIntersecting = ref(false);

const state = computed<DataLoadingWrapperState>(() => {
  if (props.loading) return DataLoadingWrapperState.Loading;
  if (props.error) return DataLoadingWrapperState.Error;

  return DataLoadingWrapperState.Done;
});

const chartComponent = computed<Component | undefined>(() =>
  appInstance.component(props.chartConfig.component)
);

const PIXEL_LENGTH = /^\d+(\.\d+)?px$/;

// Opt-in: containment stops chart mount/unmount from relaying out the whole
// document, but it is only safe where the box has a definite height. Consumer
// CSS such as Quasar's `.fit` / `.full-height` (`height: 100% !important`)
// overrides the configured height, and under size containment an unresolvable
// percentage collapses the box to 0px.
const chartStyle = computed<CSSProperties | undefined>(() => {
  const { style } = props.chartConfig;

  if (!props.isolateLayout || !PIXEL_LENGTH.test(String(style?.height))) {
    return style;
  }

  return { ...style, contain: "size layout style" };
});

const chartProps = computed<ChartLibraryProps>(() =>
  merge({}, props.chartData, props.chartConfig.componentAttributes)
);

const handleIntersection: IntersectionValue = (el): false | undefined => {
  isIntersecting.value = el.isIntersecting;
  emit("intersection", isIntersecting.value);

  if (isIntersecting.value && props.intersectOnce) {
    return false;
  }

  return undefined;
};
</script>

<style lang="scss" scoped>
.c-chart-library-chart {
  width: 100%;

  // Mounting or unmounting a chart must not dirty layout outside this box,
  // otherwise every scroll in/out forces a full-document relayout.
  &--isolated {
    contain: layout style;
  }
}

// A CSS animation on mount replaces <Transition>, whose leave hook forces a
// synchronous reflow (offsetHeight) each time a chart scrolls out of view.
.c-chart-library-chart__chart {
  animation: c-chart-library-chart-fade-in var(--fade-duration, 0.5s)
    var(--fade-timing, ease);
}

@keyframes c-chart-library-chart-fade-in {
  from {
    opacity: 0;
  }
}
</style>
